import { useCallback, useLayoutEffect, useRef } from 'react';
import { useRouter } from 'next/router';
import { parsePathAndRemoveParams } from '@/utils/helpers';
import { ROUTES } from '@/constants/routes';
import { MY_ADS_PATHS } from '@/features/Profile/core/ProfileNav/ProfileNav.constants';

let sidebar: HTMLElement | null = null;
let sidebarScrollTop = 0;
let lastSectionKey: string | null = null;
let lastSidebarTop: number | null = null;

export function useDesktopProfileScroll(isDesktop: boolean) {
  const router = useRouter();
  const isNewSessionRef = useRef(true);

  const sidebarRef = useCallback((node: HTMLElement | null) => {
    sidebar = node;
  }, []);

  const getSectionKey = (url: string) => {
    const path = parsePathAndRemoveParams(url, []).clearUrl;

    if (path === ROUTES.profile || MY_ADS_PATHS.includes(path)) {
      return 'my-ads';
    }

    return path;
  };

  useLayoutEffect(() => {
    if (!isDesktop) return;

    if (isNewSessionRef.current) {
      lastSectionKey = null;
      isNewSessionRef.current = false;
    }

    const sectionKey = getSectionKey(router.asPath);

    if (lastSectionKey !== sectionKey) {
      window.scrollTo(0, 0);
    }

    lastSectionKey = sectionKey;

    if (sidebar) {
      sidebar.scrollTop = sidebarScrollTop;
    }

    const update = () => {
      if (!sidebar) return;

      lastSidebarTop = sidebar.getBoundingClientRect().top;
    };

    router.events.on('routeChangeStart', update);
    window.addEventListener('scroll', update, { passive: true });

    return () => {
      if (sidebar) {
        sidebarScrollTop = sidebar.scrollTop;
      }

      router.events.off('routeChangeStart', update);
      window.removeEventListener('scroll', update);
    };
  }, [isDesktop, router.asPath, router.events]);

  return { sidebarRef };
}

export function useFreezeProfileSidebarWhile(active: boolean) {
  let pinnedTop: number | null = null;

  useLayoutEffect(() => {
    if (!active) return;

    const applyPinnedTop = () => {
      if (!sidebar) return;

      sidebar.style.top = pinnedTop == null ? '' : `${pinnedTop}px`;
    };

    pinnedTop =
      lastSidebarTop ?? sidebar?.getBoundingClientRect().top ?? pinnedTop;
    applyPinnedTop();

    return () => {
      pinnedTop = null;
      applyPinnedTop();
    };
  }, [active]);
}
